import React, { useEffect, useRef, useState } from "react";
import ConfirmModal from "./ConfirmModal";
import LiveCheckPanel from "./LiveCheckPanel";
import WaitingRoom from "../WaitingRoom";
import {
	IrisLeadLink,
	TERMINAL_BATCH_STATUSES,
	toastApiError,
	useBatchPolling,
	useMountedRef,
} from "./common";
import "./Import.css";

// Add contacts to EXISTING leads — the web version of what the Leadscrub MCP
// does for a scrub's "red" list (ITSM-1362 / 1366). All matching and safety
// checks run server-side (services/contact_append.py): every lead is re-read
// live, a contact is only added when its email (or, with no email, its name)
// is not already on the lead, existing contacts are never changed, and each
// write is verified. This screen only uploads/previews/confirms/reports.
// The file preview is only an estimate (the ACCA list said 2,595; live Iris
// said 1,107), so approval waits for the read-only live check and shows its
// number. The run then skips leads the check found nothing to add to.

// Plain-language reason for a problem row in the finished batch.
function problemText(row) {
	const err = String(row?.error || "");
	if (/record field must be between 1 and 100/i.test(err)) {
		return "This lead already has 100 or more contacts, and Iris won't accept more through the import. Nothing was written to it.";
	}
	if (/not found in iris/i.test(err)) return "This lead no longer exists in Iris. Nothing was written.";
	if (/^UNCONFIRMED/i.test(err)) {
		return "Iris didn't confirm this write. Ask an admin to check the lead and resume the batch.";
	}
	if (/VERIFY FAILED/i.test(err)) {
		return "After writing, the lead didn't look as expected, so the whole batch stopped. Ask an admin to check it.";
	}
	return err || "Failed";
}

export default function AddContactsFlow({ api, handoff = null, onViewHistory }) {
	const mountedRef = useMountedRef();
	const fileRef = useRef(null);
	const [upload, setUpload] = useState(null);
	const [fileName, setFileName] = useState("");
	const [uploading, setUploading] = useState(false);
	const [preview, setPreview] = useState(null);
	const [previewing, setPreviewing] = useState(false);
	const [live, setLive] = useState(null);
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [starting, setStarting] = useState(false);
	const [batchId, setBatchId] = useState(null);
	// Gate for the waiting room: true once the user clicks past the "Done"
	// screen. Reset any time a batch starts (or the flow is reset) so the
	// next batch gets its own waiting room instead of skipping straight
	// through on stale state.
	const [continued, setContinued] = useState(false);

	const { batch } = useBatchPolling(api, batchId, { enabled: Boolean(batchId) });

	const reset = () => {
		setUpload(null);
		setFileName("");
		setPreview(null);
		setLive(null);
		setBatchId(null);
		setContinued(false);
		if (fileRef.current) fileRef.current.value = "";
	};

	// A red list handed over from the scrub results panel is already staged.
	useEffect(() => {
		if (!handoff?.upload) return;
		setPreview(null);
		setLive(null);
		setBatchId(null);
		setUpload(handoff.upload);
		setFileName(handoff.fileName || handoff.upload.filename || "");
	}, [handoff]);

	// Every newly staged file gets a fresh dry run.
	useEffect(() => {
		if (!upload?.upload_id) return;
		let cancelled = false;
		setPreviewing(true);
		api
			.previewContacts(upload.upload_id)
			.then((res) => {
				if (!cancelled) setPreview(res);
			})
			.catch((err) => toastApiError(err, "Preview failed"))
			.finally(() => {
				if (!cancelled) setPreviewing(false);
			});
		return () => {
			cancelled = true;
		};
	}, [api, upload]);

	const onFile = async (e) => {
		const file = e.target.files && e.target.files[0];
		if (!file) return;
		setUploading(true);
		setPreview(null);
		setLive(null);
		setBatchId(null);
		try {
			const res = await api.uploadCsv(file);
			if (!mountedRef.current) return;
			setUpload(res);
			setFileName(file.name);
		} catch (err) {
			toastApiError(err, "Upload failed");
		} finally {
			if (mountedRef.current) setUploading(false);
		}
	};

	const start = async () => {
		setStarting(true);
		try {
			const res = await api.executeContacts(upload.upload_id);
			if (!mountedRef.current) return;
			setBatchId(res.batch_id);
			setContinued(false);
			setConfirmOpen(false);
		} catch (err) {
			toastApiError(err, "Could not start");
		} finally {
			if (mountedRef.current) setStarting(false);
		}
	};

	const errors = (preview?.issues || []).filter((i) => i.level === "error");
	const warnings = (preview?.issues || []).filter((i) => i.level !== "error");
	const likelyNew = preview?.likely_new ?? 0;
	const liveReady = live?.status === "done" && live?.fresh;
	const realNew = liveReady ? live.would_add ?? 0 : 0;

	// ---------------------------------------------------------- running / done
	if (batchId) {
		const leads = batch?.leads || [];
		const done = leads.filter((l) => l.status !== "pending").length;
		const total = batch?.total_leads || leads.length || 0;
		const finished = batch && TERMINAL_BATCH_STATUSES.has(batch.status);
		const result = batch?.params?.result || {};
		const problems = leads.filter(
			(l) => l.status === "failed" || (l.status === "pending" && l.error) || /VERIFY FAILED/.test(l.error || "")
		);
		const doneMessage = finished
			? problems.length
				? `Done — finished with ${problems.length} lead(s) that need a look.`
				: `Done — ${result.contacts_added ?? 0} contact${
						(result.contacts_added ?? 0) === 1 ? "" : "s"
				  } added.`
			: undefined;
		return (
			<div className="importCard">
				<h2>Adding contacts — {fileName}</h2>
				{(!finished || !continued) && (
					<WaitingRoom
						active={!finished}
						done={Boolean(finished)}
						title="Adding contacts in Iris…"
						progress={{ value: done, max: total }}
						doneMessage={doneMessage}
						onContinue={() => setContinued(true)}
					/>
				)}
				{!finished && (
					<p className="importMuted">
						Each lead is re-checked in Iris before anything is added. You can leave this page;
						the batch keeps running and shows up in Import History.
					</p>
				)}
				{finished && continued && (
					<>
						<div className={`resultBanner ${problems.length ? "error" : ""}`}>
							{problems.length
								? `Finished with ${problems.length} lead(s) that need a look.`
								: "Finished."}
						</div>
						<div className="previewKV" style={{ margin: "12px 0" }}>
							<div className="kv">
								<b>{result.contacts_added ?? 0}</b> contacts added to{" "}
								<b>{result.leads_updated ?? 0}</b> leads
							</div>
							<div className="kv">
								<b>{result.contacts_skipped ?? 0}</b> skipped (already on the lead)
							</div>
							{problems.length > 0 && (
								<div className="kv">
									<b>{problems.length}</b> not added
								</div>
							)}
						</div>
						{problems.length > 0 && (
							<div className="importTableWrap">
								<table className="importTable">
									<thead>
										<tr>
											<th>Lead</th>
											<th>What happened</th>
										</tr>
									</thead>
									<tbody>
										{problems.map((l) => (
											<tr key={l.id}>
												<td>
													<IrisLeadLink leadId={l.iris_lead_id}>{l.group_key}</IrisLeadLink>
												</td>
												<td>{problemText(l)}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
						<div className="wizardNav">
							<button type="button" className="importBtn secondary" onClick={reset}>
								Add contacts from another file
							</button>
							{onViewHistory && (
								<button type="button" className="importBtn" onClick={() => onViewHistory(batchId)}>
									Open in Import History
								</button>
							)}
						</div>
					</>
				)}
			</div>
		);
	}

	// ------------------------------------------------------------- choose file
	if (!upload) {
		return (
			<div className="importCard">
				<h2>Add contacts to existing leads</h2>
				<p className="importMuted">
					For a scrub's <b>red</b> list (companies already in Iris). New contacts are added to the
					lead each row matched; contacts already on the lead are skipped, and nothing existing is
					changed. The file needs the scrub's <code>existing_lead_id</code> column.
				</p>
				<input
					ref={fileRef}
					type="file"
					accept=".csv,.xlsx"
					onChange={onFile}
					disabled={uploading}
				/>
				{uploading && <p className="importMuted">Uploading…</p>}
			</div>
		);
	}

	// ---------------------------------------------------------------- preview
	return (
		<div className="importCard">
			<h2>Add contacts — {fileName}</h2>
			{previewing && <p className="importMuted">Checking the file…</p>}
			{preview && (
				<>
					<p className="fileEstimate">
						File: {Number(preview.total_leads || 0).toLocaleString()} existing leads,{" "}
						{Number(preview.planned_contacts ?? likelyNew).toLocaleString()} contacts listed. The real number comes from the live check below.
					</p>
					{errors.map((i, n) => (
						<div key={`e${n}`} className="importError">
							{i.message}
						</div>
					))}
					{warnings.length > 0 && (
						<details style={{ margin: "8px 0" }}>
							<summary>{warnings.length} row(s) in the file will be skipped</summary>
							<ul>
								{warnings.map((i, n) => (
									<li key={`w${n}`} className="importMuted">
										{i.message}
									</li>
								))}
							</ul>
						</details>
					)}
					{errors.length === 0 && (
						<LiveCheckPanel api={api} uploadId={upload.upload_id} onResult={setLive} />
					)}
				</>
			)}
			<div className="wizardNav">
				<button type="button" className="importBtn secondary" onClick={reset}>
					Use a different file
				</button>
				<button
					type="button"
					className="importBtn"
					disabled={!preview || previewing || errors.length > 0 || !liveReady || realNew === 0}
					onClick={() => setConfirmOpen(true)}>
					{!liveReady
						? "Waiting for the live check…"
						: realNew === 0
						? "Nothing new to add"
						: `Add ${realNew.toLocaleString()} new contact${realNew === 1 ? "" : "s"}`}
				</button>
			</div>
			<ConfirmModal
				open={confirmOpen}
				title="Add these contacts in Iris?"
				confirmLabel="Add contacts"
				confirmStyle="primary"
				busy={starting}
				onConfirm={start}
				onCancel={() => setConfirmOpen(false)}>
				<p>
					<b>{realNew.toLocaleString()}</b> new contacts will be added to{" "}
					<b>{(live?.leads_getting_contacts ?? 0).toLocaleString()}</b> existing leads. The{" "}
					{(live?.leads_with_nothing_to_add ?? 0).toLocaleString()} leads with nothing new are skipped.
					Each lead is re-checked right before writing, existing contacts are not changed, and the
					batch can be undone from Import History.
				</p>
			</ConfirmModal>
		</div>
	);
}
